import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Package, ShoppingBag, Download, Search, ChevronDown, Calendar } from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import { exportToExcel } from '@/lib/export-utils';
import toast from 'react-hot-toast';
import type { OrderStatus } from '@/types';

const STATUS_COLORS: Record<OrderStatus, string> = {
  pending: 'bg-yellow-500/10 text-yellow-500',
  confirmed: 'bg-blue-500/10 text-blue-500',
  processing: 'bg-purple-500/10 text-purple-500',
  shipped: 'bg-indigo-500/10 text-indigo-500',
  delivered: 'bg-green-500/10 text-green-500',
  cancelled: 'bg-red-500/10 text-red-500',
};

const STATUS_DOTS: Record<OrderStatus, string> = {
  pending: 'bg-yellow-500',
  confirmed: 'bg-blue-500',
  processing: 'bg-purple-500',
  shipped: 'bg-indigo-500',
  delivered: 'bg-green-500',
  cancelled: 'bg-red-500',
};

const ALL_STATUSES: Array<OrderStatus | 'all'> = ['all', 'pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled'];

const AdminOrdersPage: React.FC = () => {
  const orders = useAdminStore((s) => s.orders);
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<OrderStatus | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showExportOptions, setShowExportOptions] = useState(false);

  const filtered = useMemo(() => {
    let items = [...(orders ?? [])].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    if (statusFilter !== 'all') { items = items.filter((o) => o.status === statusFilter); }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      items = items.filter((o) =>
        o._id.toLowerCase().includes(q) ||
        `${o.customer.firstName} ${o.customer.lastName}`.toLowerCase().includes(q) ||
        o.customer.email.toLowerCase().includes(q)
      );
    }
    if (dateFrom) { items = items.filter((o) => new Date(o.createdAt) >= new Date(dateFrom)); }
    if (dateTo) { items = items.filter((o) => new Date(o.createdAt) <= new Date(dateTo + 'T23:59:59')); }
    return items;
  }, [orders, statusFilter, searchQuery, dateFrom, dateTo]);

  const formatCurrency = (amount: number, currency: string): string => {
    if (currency === 'GEL') return (amount / 100).toLocaleString() + ' GEL';
    return '$' + (amount / 100).toLocaleString();
  };

  const formatDate = (date: string): string => {
    return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const truncateId = (id: string): string => {
    return id.length > 8 ? id.slice(0, 8) + '...' : id;
  };

  const handleExportOrders = (): void => {
    if (!filtered.length) { toast.error('No orders to export'); return; }
    const data = filtered.map((o) => ({
      'Order ID': o._id,
      'Date': formatDate(o.createdAt),
      'Customer': `${o.customer.firstName} ${o.customer.lastName}`,
      'Email': o.customer.email,
      'Phone': o.customer.phone || '',
      'Items': o.items.map((i) => `${i.artworkTitle?.en || 'Artwork'} x${i.quantity}`).join(', '),
      'Items Count': o.items.length,
      'Subtotal': formatCurrency(o.subtotal, o.currency),
      'Shipping': formatCurrency(o.shipping, o.currency),
      'Tax': formatCurrency(o.tax, o.currency),
      'Total': formatCurrency(o.total, o.currency),
      'Currency': o.currency,
      'Status': o.status.toUpperCase(),
      // The delivery address lives on shippingAddress; customer holds a copy that predates
      // address2 and state, so it is only the fallback.
      'Address': o.shippingAddress?.address || o.customer.address || '',
      'Address 2': o.shippingAddress?.address2 || '',
      'City': o.shippingAddress?.city || o.customer.city || '',
      'State': o.shippingAddress?.state || '',
      'Postal Code': o.shippingAddress?.postalCode || o.customer.postalCode || '',
      'Country': o.shippingAddress?.country || o.customer.country || '',
      'Tracking ID': o.trackingId || '',
    }));
    exportToExcel(data, { filename: `orders-${new Date().toISOString().slice(0, 10)}`, sheetName: 'Orders' });
    toast.success(`Exported ${filtered.length} orders`);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-syncopate font-bold tracking-tighter">Orders</h1>
          <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.3em] text-black/20 dark:text-white/20">{(orders ?? []).length} total</p>
        </div>
        <div className="relative">
          <button onClick={() => setShowExportOptions(!showExportOptions)} className="flex items-center gap-2 px-4 py-2.5 bg-black/[0.05] hover:bg-black/[0.1] text-[10px] font-bold uppercase tracking-wider rounded-xl transition-all">
            <Download size={14} /> Export {filtered.length} <ChevronDown size={12} />
          </button>
          <AnimatePresence>
            {showExportOptions && (
              <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }} className="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-[#111] border border-black/10 dark:border-white/10 rounded-xl shadow-xl z-50 p-3 space-y-2">
                <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30">Export filtered results</p>
                <p className="text-[11px] text-black/60 dark:text-white/60">{filtered.length} orders match current filters</p>
                <button onClick={() => { handleExportOrders(); setShowExportOptions(false); }} className="w-full py-2.5 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-wider rounded-lg">
                  Export {filtered.length} Orders
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Search & Date filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-black/20 dark:text-white/20" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 bg-black/[0.03] dark:bg-white/[0.05] border border-black/[0.06] dark:border-white/[0.08] rounded-xl text-sm font-medium outline-none focus:border-black/20 dark:focus:border-white/20 transition-all placeholder:text-black/20 dark:placeholder:text-white/20"
            placeholder="Search by ID, name, email..."
          />
        </div>
        <div className="flex items-center gap-2">
          <Calendar size={13} className="text-black/30 dark:text-white/30" />
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="px-3 py-2 bg-black/[0.03] dark:bg-white/[0.05] border border-black/[0.06] dark:border-white/[0.08] rounded-xl text-[11px] font-medium outline-none" />
          <span className="text-[10px] text-black/30 dark:text-white/30">to</span>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="px-3 py-2 bg-black/[0.03] dark:bg-white/[0.05] border border-black/[0.06] dark:border-white/[0.08] rounded-xl text-[11px] font-medium outline-none" />
        </div>
      </div>

      <div className="flex items-center gap-1 overflow-x-auto pb-1">
        {ALL_STATUSES.map((status) => {
          const count = status === 'all' ? (orders ?? []).length : (orders ?? []).filter((o) => o.status === status).length;
          return (
            <button key={status} onClick={() => setStatusFilter(status)} className={`flex items-center gap-1.5 px-3 py-1.5 text-[9px] font-bold uppercase tracking-[0.1em] whitespace-nowrap rounded-lg transition-all ${statusFilter === status ? 'bg-black dark:bg-white text-white dark:text-black' : 'text-black/30 dark:text-white/30 hover:text-black/60 dark:hover:text-white/60'}`}>
              {status !== 'all' && <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOTS[status]}`} />}
              {status === 'all' ? 'All' : status}
              <span className="opacity-50">({count})</span>
            </button>
          );
        })}
      </div>

      {filtered.length > 0 ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-black/[0.04] dark:border-white/[0.04]">
                {['Order ID', 'Customer', 'Items', 'Total', 'Status', 'Date'].map((h) => (
                  <th key={h} className="text-left px-4 py-3 text-[9px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((order) => (
                <tr key={order._id} onClick={() => navigate(`/admin/orders/${order._id}`)} className="border-b border-black/[0.02] dark:border-white/[0.02] hover:bg-black/[0.02] dark:hover:bg-white/[0.02] cursor-pointer transition-colors">
                  <td className="px-4 py-3"><span className="text-[11px] font-mono font-bold text-black/50 dark:text-white/50">{order.orderNumber || truncateId(order._id)}</span></td>
                  <td className="px-4 py-3"><span className="text-[12px] font-medium">{order.customer?.firstName} {order.customer?.lastName}</span></td>
                  <td className="px-4 py-3"><span className="text-[11px] text-black/50 dark:text-white/50">{(order.items ?? []).length} item{(order.items ?? []).length !== 1 ? 's' : ''}</span></td>
                  <td className="px-4 py-3"><span className="text-[12px] font-bold">{formatCurrency(order.total, order.currency)}</span></td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-wider ${STATUS_COLORS[order.status]}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOTS[order.status]}`} />
                      {order.status}
                    </span>
                  </td>
                  <td className="px-4 py-3"><span className="text-[10px] text-black/40 dark:text-white/40">{formatDate(order.createdAt)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </motion.div>
      ) : (
        <div className="text-center py-16">
          <div className="w-16 h-16 rounded-2xl bg-black/[0.03] dark:bg-white/[0.03] flex items-center justify-center mx-auto mb-4">
            {(orders ?? []).length === 0 ? <ShoppingBag size={24} className="text-black/10 dark:text-white/10" /> : <Package size={24} className="text-black/10 dark:text-white/10" />}
          </div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-black/20 dark:text-white/20">
            {(orders ?? []).length === 0 ? 'No orders yet' : 'No orders match this filter'}
          </p>
          {(orders ?? []).length === 0 && <p className="text-[10px] text-black/15 dark:text-white/15 mt-1">Orders will appear here when customers make purchases</p>}
        </div>
      )}
    </div>
  );
};

export default AdminOrdersPage;
